/**
 * Utilities for handling GraphQL Relay node IDs.
 *
 * Relay IDs are base64-encoded strings in the format: base64("{Type}:{raw_id}")
 * For example: "Conversation:01992afc-b16e-7e44-ad9d-05de0b1e0b3c" becomes "Q29udmVyc2F0aW9uOjAxOTkyYWZjLWIxNmUtN2U0NC1hZDlkLTA1ZGUwYjFlMGIzYw=="
 */

/**
 * Encode a raw UUID to a Relay node ID.
 * @param type - The GraphQL type name (e.g., "Workflow", "Conversation")
 * @param rawId - The raw UUID
 * @returns The base64-encoded Relay ID
 */
export const encodeRelayId = (type: string, rawId: string): string => {
  return btoa(`${type}:${rawId}`);
};

/**
 * Decode a Relay node ID to extract the raw UUID.
 * Returns the original ID if it's not a valid base64-encoded Relay ID.
 */
export const decodeRelayId = (id: string): string => {
  try {
    // Try to decode as base64
    const decoded = atob(id);

    // Check if it has the expected format: "Type:uuid"
    const colonIndex = decoded.indexOf(':');
    if (colonIndex > 0) {
      // Extract the raw ID part after the colon
      return decoded.substring(colonIndex + 1);
    }

    // If not in expected format, return original
    return id;
  } catch {
    // If base64 decoding fails, return original
    return id;
  }
};

// Returns the nodes list from a given edges list from a GraphQL response using Relay pagination
// (also known as "Connections"). @see https://relay.dev/graphql/connections.htm
export const getNodes = <T>(
  edges?: ({ node?: T | null } | null)[] | null
): T[] | null | undefined => edges?.filter(e => !!e?.node).map(e => e!.node!);
